<script setup>
import { ref, onMounted } from 'vue';
import segueDashboardAPI from 'dashboard/api/segueDashboard';
import PeriodFilter from './components/PeriodFilter.vue';
import ContratosTable from './components/ContratosTable.vue';

// v91: Contratos com o RESUMO do dashboard-leads em cima:
//   - modo GERAL: cards destaque (Pago / Em Análise / Cancelado) + matriz
//     status x FACTA/UY3/Total + a lista de contratos embaixo.
//   - modo VENDEDOR: matriz vendedor x status com (n) CLICÁVEL → janela com
//     os contratos individuais (Banco, Nome, CPF, Telefone, Digitação,
//     Pagamento, Valor). C6: pendente.
const period = ref('today');
const customFrom = ref(null);
const customTo = ref(null);
const bank = ref('all');
const refreshKey = ref(0);
const modo = ref('geral'); // geral | vendedor

const resumo = ref(null);
const resumoLoading = ref(false);
const resumoError = ref(null);

// janela de drill-down
const detalhe = ref(null);
const detalheLoading = ref(false);
const detalheTitulo = ref('');

const BANKS = [
  { key: 'all', label: 'Todos' },
  { key: 'uy3', label: 'UY3' },
  { key: 'facta', label: 'FACTA' },
];

const CATEGORIAS_COLS = [
  { key: 'Pagos', label: 'Pago' },
  { key: 'Aguardando Pagamento', label: 'Ag. Pagto' },
  { key: 'Aguardando Assinatura', label: 'Ag. Assin' },
  { key: 'Em Análise', label: 'Em Análise' },
  { key: 'Cancelados', label: 'Cancelado' },
  { key: 'Reprovados', label: 'Reprovado' },
];

const CAT_DOT = {
  Pagos: 'bg-n-teal-9',
  'Aguardando Pagamento': 'bg-n-blue-9',
  'Aguardando Assinatura': 'bg-n-iris-9',
  'Em Análise': 'bg-n-amber-9',
  Cancelados: 'bg-n-ruby-9',
  Reprovados: 'bg-n-amber-11',
  Outros: 'bg-n-slate-9',
  TOTAL: '',
};

const loadResumo = async () => {
  resumoLoading.value = true;
  resumoError.value = null;
  try {
    const { data: resp } = await segueDashboardAPI.fetchContratosResumo({
      period: period.value,
      from: customFrom.value,
      to: customTo.value,
    });
    if (resp.error) resumoError.value = resp.error;
    else resumo.value = resp;
  } catch (e) {
    resumoError.value = e.message || 'Falha ao carregar resumo';
  } finally {
    resumoLoading.value = false;
  }
};

onMounted(loadResumo);

const onPeriodChange = params => {
  period.value = params.period;
  customFrom.value = params.from || null;
  customTo.value = params.to || null;
  loadResumo();
};

const refresh = () => {
  refreshKey.value += 1;
  loadResumo();
};

const abrirDetalhe = async (vendedor, categoria, label) => {
  detalheLoading.value = true;
  detalheTitulo.value = `${vendedor.nome} · ${label || 'Todos os status'}`;
  detalhe.value = { contratos: [] };
  try {
    const { data: resp } =
      await segueDashboardAPI.fetchContratosVendedorDetalhe({
        vendedorId: vendedor.id,
        categoria: categoria || '__total__',
        period: period.value,
        from: customFrom.value,
        to: customTo.value,
      });
    detalhe.value = resp;
  } catch (e) {
    detalhe.value = { error: e.message };
  } finally {
    detalheLoading.value = false;
  }
};

const fecharDetalhe = () => {
  detalhe.value = null;
};

const brl = v =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtRange = () => {
  const p = resumo.value?.period;
  if (!p) return '';
  const f = d => d.split('-').reverse().join('/');
  return `${f(p.from)} — ${f(p.to)}`;
};

const fmtDate = v => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
};

const bankChip = b =>
  b === 'FACTA' ? 'bg-n-amber-3 text-n-amber-11' : 'bg-n-blue-3 text-n-blue-11';
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="overflow-auto p-4 w-full h-full bg-n-background">
    <div class="flex flex-col gap-5 w-full">
      <!-- Header -->
      <div class="flex flex-wrap gap-4 justify-between items-start">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">Contratos</h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Status dos contratos (UY3 + FACTA) · {{ fmtRange() }}
          </p>
        </div>
        <div class="flex flex-wrap gap-3 items-center">
          <PeriodFilter v-model="period" @change="onPeriodChange" />
          <div class="flex overflow-hidden rounded-lg border border-n-weak">
            <button
              v-for="b in BANKS"
              :key="b.key"
              type="button"
              class="px-3 py-1.5 text-sm font-medium"
              :class="
                bank === b.key
                  ? 'bg-n-brand text-white'
                  : 'bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2'
              "
              @click="bank = b.key"
            >
              {{ b.label }}
            </button>
          </div>
          <!-- Geral | Vendedor -->
          <div class="flex overflow-hidden rounded-lg border border-n-weak">
            <button
              type="button"
              class="px-4 py-1.5 text-sm font-semibold"
              :class="
                modo === 'geral'
                  ? 'bg-n-teal-9 text-white'
                  : 'bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2'
              "
              @click="modo = 'geral'"
            >
              Resumo
            </button>
            <button
              type="button"
              class="px-4 py-1.5 text-sm font-semibold"
              :class="
                modo === 'vendedor'
                  ? 'bg-n-teal-9 text-white'
                  : 'bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2'
              "
              @click="modo = 'vendedor'"
            >
              Por vendedor
            </button>
          </div>
          <button
            type="button"
            class="flex gap-1.5 items-center px-4 h-9 text-sm font-semibold text-white rounded-lg shadow-sm bg-n-brand hover:opacity-90"
            @click="refresh"
          >
            <span class="i-lucide-refresh-cw size-4" />
            Atualizar
          </button>
        </div>
      </div>

      <p v-if="resumoError" class="text-sm text-n-ruby-11">{{ resumoError }}</p>
      <p v-if="resumoLoading && !resumo" class="text-sm text-n-slate-11">
        Carregando resumo…
      </p>

      <!-- ============ MODO RESUMO (GERAL) ============ -->
      <template v-if="modo === 'geral' && resumo">
        <!-- Cards destaque -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div class="p-4 rounded-xl border-l-4 border border-n-weak border-l-n-teal-9 bg-n-solid-1">
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              Pago
            </span>
            <p class="mt-1 text-2xl font-bold tabular-nums text-n-teal-11">
              {{ brl(resumo.cards?.pago?.valor) }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ resumo.cards?.pago?.qtd || 0 }} contratos
            </p>
          </div>
          <div class="p-4 rounded-xl border-l-4 border border-n-weak border-l-n-amber-9 bg-n-solid-1">
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              Em Análise
            </span>
            <p class="mt-1 text-2xl font-bold tabular-nums text-n-amber-11">
              {{ brl(resumo.cards?.em_analise?.valor) }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ resumo.cards?.em_analise?.qtd || 0 }} contratos
            </p>
          </div>
          <div class="p-4 rounded-xl border-l-4 border border-n-weak border-l-n-ruby-9 bg-n-solid-1">
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              Cancelado
            </span>
            <p class="mt-1 text-2xl font-bold tabular-nums text-n-ruby-11">
              {{ brl(resumo.cards?.cancelado?.valor) }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ resumo.cards?.cancelado?.qtd || 0 }} contratos
            </p>
          </div>
        </div>

        <!-- Matriz status x banco -->
        <div class="overflow-x-auto rounded-xl border border-n-weak">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left bg-n-solid-2 text-n-slate-11">
                <th class="px-3 py-2.5 font-medium">Status</th>
                <th class="px-3 py-2.5 font-medium text-right">FACTA</th>
                <th class="px-3 py-2.5 font-medium text-right">UY3</th>
                <th class="px-3 py-2.5 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="l in resumo.matriz"
                :key="l.categoria"
                class="border-t border-n-weak"
                :class="l.categoria === 'TOTAL' ? 'bg-n-solid-1 font-semibold' : ''"
              >
                <td class="px-3 py-2.5 text-n-slate-12">
                  <span class="flex gap-2 items-center">
                    <span
                      v-if="l.categoria !== 'TOTAL'"
                      class="rounded-full size-2"
                      :class="CAT_DOT[l.categoria] || 'bg-n-slate-9'"
                    />
                    {{ l.categoria }}
                  </span>
                </td>
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                  <template v-if="l.facta.qtd">
                    {{ brl(l.facta.valor) }}
                    <span class="text-xs text-n-slate-11">({{ l.facta.qtd }})</span>
                  </template>
                  <template v-else>—</template>
                </td>
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                  <template v-if="l.uy3.qtd">
                    {{ brl(l.uy3.valor) }}
                    <span class="text-xs text-n-slate-11">({{ l.uy3.qtd }})</span>
                  </template>
                  <template v-else>—</template>
                </td>
                <td class="px-3 py-2.5 font-semibold text-right tabular-nums text-n-slate-12">
                  {{ brl(l.total.valor) }}
                  <span class="text-xs font-normal text-n-slate-11">
                    ({{ l.total.qtd }})
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Lista de contratos (a tabela de sempre) -->
        <ContratosTable
          :period="period"
          :from="customFrom"
          :to="customTo"
          :bank="bank"
          :refresh-key="refreshKey"
        />
      </template>

      <!-- ============ MODO POR VENDEDOR ============ -->
      <template v-else-if="modo === 'vendedor' && resumo">
        <div class="overflow-x-auto rounded-xl border border-n-weak">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left bg-n-solid-2 text-n-slate-11">
                <th class="px-3 py-2.5 font-medium">Vendedor</th>
                <th
                  v-for="c in CATEGORIAS_COLS"
                  :key="c.key"
                  class="px-3 py-2.5 font-medium text-right"
                >
                  {{ c.label }}
                </th>
                <th class="px-3 py-2.5 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="v in resumo.vendedores"
                :key="v.id"
                class="border-t border-n-weak hover:bg-n-alpha-1"
              >
                <td class="px-3 py-2.5 font-medium text-n-slate-12">
                  {{ v.nome }}
                </td>
                <td
                  v-for="c in CATEGORIAS_COLS"
                  :key="c.key"
                  class="px-3 py-2.5 text-right tabular-nums text-n-slate-12"
                >
                  <template v-if="v.cats[c.key] && v.cats[c.key].qtd">
                    {{ brl(v.cats[c.key].valor) }}
                    <button
                      type="button"
                      class="ml-0.5 text-xs font-semibold underline text-n-teal-11 hover:opacity-75"
                      :title="`Ver os ${v.cats[c.key].qtd} contrato(s)`"
                      @click="abrirDetalhe(v, c.key, c.label)"
                    >
                      ({{ v.cats[c.key].qtd }})
                    </button>
                  </template>
                  <template v-else>—</template>
                </td>
                <td class="px-3 py-2.5 font-semibold text-right tabular-nums text-n-slate-12">
                  {{ brl(v.total.valor) }}
                  <button
                    type="button"
                    class="ml-0.5 text-xs font-semibold underline text-n-teal-11 hover:opacity-75"
                    @click="abrirDetalhe(v, '__total__', null)"
                  >
                    ({{ v.total.qtd }})
                  </button>
                </td>
              </tr>
              <tr v-if="!resumo.vendedores?.length">
                <td :colspan="CATEGORIAS_COLS.length + 2" class="px-3 py-6 text-center text-n-slate-11">
                  Sem contratos no período
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- ============ JANELA DE DETALHE ============ -->
      <div
        v-if="detalhe"
        class="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/60"
        @click.self="fecharDetalhe"
      >
        <div class="flex flex-col w-full max-w-4xl max-h-[85vh] rounded-xl border shadow-xl border-n-weak bg-n-background">
          <div class="flex justify-between items-center px-4 py-3 border-b border-n-weak">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ detalheTitulo }}
              <span v-if="detalhe.total !== undefined" class="font-normal text-n-slate-11">
                · {{ detalhe.total }} contrato(s)
              </span>
            </h3>
            <button
              type="button"
              class="flex gap-1 items-center px-3 py-1 text-xs font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
              @click="fecharDetalhe"
            >
              ✕ Fechar
            </button>
          </div>
          <div class="overflow-auto p-4">
            <p v-if="detalheLoading" class="text-sm text-n-slate-11">
              Carregando…
            </p>
            <p v-else-if="detalhe.error" class="text-sm text-n-ruby-11">
              {{ detalhe.error }}
            </p>
            <table v-else class="w-full text-sm">
              <thead>
                <tr class="text-left bg-n-solid-2 text-n-slate-11">
                  <th class="px-3 py-2 font-medium">Banco</th>
                  <th class="px-3 py-2 font-medium">Nome</th>
                  <th class="px-3 py-2 font-medium">CPF</th>
                  <th class="px-3 py-2 font-medium">Telefone</th>
                  <th class="px-3 py-2 font-medium">Digitação</th>
                  <th class="px-3 py-2 font-medium">Pagamento</th>
                  <th class="px-3 py-2 font-medium text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(ct, i) in detalhe.contratos"
                  :key="i"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2">
                    <span
                      class="px-2 py-0.5 text-xs font-semibold rounded"
                      :class="bankChip(ct.banco)"
                    >
                      {{ ct.banco }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">{{ ct.nome }}</td>
                  <td class="px-3 py-2 tabular-nums text-n-slate-12">{{ ct.cpf }}</td>
                  <td class="px-3 py-2 text-n-slate-12">{{ ct.telefone }}</td>
                  <td class="px-3 py-2 tabular-nums text-n-slate-12">
                    {{ fmtDate(ct.data_digitacao) }}
                  </td>
                  <td class="px-3 py-2 tabular-nums text-n-slate-12">
                    {{ fmtDate(ct.data_pagamento) }}
                  </td>
                  <td class="px-3 py-2 font-semibold text-right tabular-nums text-n-slate-12">
                    {{ brl(ct.valor) }}
                  </td>
                </tr>
                <tr v-if="!detalhe.contratos?.length">
                  <td colspan="7" class="px-3 py-6 text-center text-n-slate-11">
                    Nenhum contrato
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
